import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, Chip, Container, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useNavigate } from 'react-router-dom'

import { useLanguage } from '../contexts/LanguageContext'
import { useNotification } from '../contexts/NotificationContext'

const notificationData = (item) => item?.data && typeof item.data === 'object' ? item.data : {}

const notificationActionUrl = (item) => {
    const data = notificationData(item)
    if ((item?.type === 'service_pro_message_received' || data.event === 'service_pro_message_received') && data.request_id) {
        return data.recipient_role === 'provider'
            ? '/service-pros/manage'
            : `/service-pros/requests/${encodeURIComponent(data.request_id)}`
    }
    if ((item?.type === 'service_pro_estimate_received' || data.event === 'service_pro_estimate_received') && data.request_id) {
        return `/service-pros/requests/${encodeURIComponent(data.request_id)}`
    }
    return item?.action_url || ''
}

const localizedValue = (value, lang, fallback = '') => {
    if (value && typeof value === 'object') {
        return value[lang] || value.en || fallback
    }
    return value || fallback
}

const notificationTitle = (item, lang) => {
    const data = notificationData(item)
    return localizedValue(data.title_i18n, lang, item.title || item.subject || item.heading || item.type || 'Notification')
}

const scrubIp = (message) =>
    String(message || '').replace(/\s*IP:\s*(?:\d{1,3}\.){3}\d{1,3}\.?/g, '').trim()

const notificationBody = (item, lang) => {
    const data = notificationData(item)
    const body = localizedValue(data.message_i18n, lang, item.message || item.body || item.description || 'MojoPayr activity update.')
    return scrubIp(body)
}

const isSecurityNotification = (item) => {
    const data = notificationData(item)
    return item.type === 'security' || data.event === 'login' || data.visual_variant === 'security'
}

const notificationActionLabel = (item, lang) => {
    const data = notificationData(item)
    if (item?.type === 'service_pro_message_received' || data.event === 'service_pro_message_received') {
        return lang === 'fr' ? 'Voir le message' : 'View message'
    }
    return localizedValue(data.action_i18n, lang, isSecurityNotification(item) ? (lang === 'fr' ? 'Voir les details' : 'View Details') : (notificationActionUrl(item) ? (lang === 'fr' ? 'Voir le devis' : 'View estimate') : ''))
}

const notificationTime = (item) => {
    const value = item.created_at || item.createdAt || item.timestamp
    if (!value) return ''
    try {
        return new Date(value).toLocaleString()
    } catch {
        return ''
    }
}

const notificationGroup = (item) => {
    if (['Today', 'Yesterday', 'Earlier'].includes(item?.group)) return item.group
    const value = item.created_at || item.createdAt || item.timestamp
    const date = value ? new Date(value) : new Date()
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const day = new Date(date); day.setHours(0, 0, 0, 0)
    if (day.getTime() === today.getTime()) return 'Today'
    if (day.getTime() === today.getTime() - 86400000) return 'Yesterday'
    return 'Earlier'
}

export default function Notifications() {
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const { notifications, unreadCount, loadNotifications, markAllRead } = useNotification()
    const [detailsItem, setDetailsItem] = useState(null)

    useEffect(() => {
        loadNotifications({ platform: 'website', locale: lang }).catch(() => {})
    }, [loadNotifications, lang])

    const rows = Array.isArray(notifications) ? notifications : []

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Card
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                        boxShadow: '0 28px 60px rgba(49,26,137,0.38)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography sx={{ color: 'rgba(255,255,255,0.74)', mb: 1 }}>
                                    {isFr ? 'Centre notifications' : 'Notification Center'}
                                </Typography>
                                <Typography sx={{ color: '#FFFFFF', fontWeight: 900, fontSize: { xs: 30, md: 42 } }}>
                                    {isFr ? 'Boite MojoPayr' : 'MojoPayr Inbox'}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.80)', mt: 1, maxWidth: 760 }}>
                                    {isFr
                                        ? 'Suivez les alertes de compte, factures, tickets, paiements et activite marchand au meme endroit.'
                                        : 'Track account alerts, invoices, tickets, payments, and merchant activity in one place.'}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    label={`${unreadCount || 0} ${isFr ? 'non lues' : 'unread'}`}
                                    sx={{ color: '#FFF', bgcolor: 'rgba(255,255,255,0.14)' }}
                                />
                                <Chip
                                    label={isFr ? 'Activite compte' : 'Account activity'}
                                    sx={{ color: '#FFF', bgcolor: 'rgba(255,255,255,0.14)' }}
                                />
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(27,16,64,0.96)',
                        border: '1px solid rgba(183,166,255,0.16)',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                            <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                                {isFr ? 'Notifications recentes' : 'Recent Notifications'}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={markAllRead}
                                sx={{
                                    color: '#FFFFFF',
                                    borderColor: 'rgba(183,166,255,0.34)',
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    fontWeight: 800,
                                }}
                            >
                                {isFr ? 'Tout marquer lu' : 'Mark All Read'}
                            </Button>
                        </Stack>

                        {rows.length === 0 ? (
                            <Stack spacing={1.5} sx={{ py: 4, alignItems: 'center', textAlign: 'center' }}>
                                <NotificationsNoneRoundedIcon sx={{ color: '#FF8A00', fontSize: 44 }} />
                                <Typography sx={{ color: '#FFFFFF', fontWeight: 900 }}>
                                    {isFr ? 'Aucune notification pour le moment' : 'No notifications yet'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', maxWidth: 560 }}>
                                    {isFr
                                        ? 'Les nouvelles inscriptions, factures, tickets, paiements, retraits et alertes importantes apparaitront ici.'
                                        : 'New signups, invoices, tickets, payments, withdrawals, and important alerts will appear here.'}
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={1.4}>
                                {rows.map((item, index) => {
                                    const unread = !item.read_at && item.read !== true
                                    const security = isSecurityNotification(item)
                                    const group = notificationGroup(item)
                                    const showGroup = index === 0 || notificationGroup(rows[index - 1]) !== group
                                    return (
                                        <React.Fragment key={item.id || `${notificationTitle(item, lang)}-${notificationTime(item)}`}>
                                        {showGroup && <Typography sx={{fontWeight:900,fontSize:18,pt:index===0?0:2,pb:.5}}>{group}</Typography>}
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 4,
                                                bgcolor: security
                                                    ? 'rgba(30,15,42,0.98)'
                                                    : unread ? 'rgba(255,138,0,0.10)' : 'rgba(91,46,255,0.10)',
                                                border: security
                                                    ? '1px solid rgba(255,122,26,0.35)'
                                                    : unread
                                                        ? '1px solid rgba(255,138,0,0.28)'
                                                        : '1px solid rgba(183,166,255,0.16)',
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <Box
                                                    sx={{
                                                        color: '#FF8A00',
                                                        mt: 0.3,
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: 3,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        bgcolor: security ? 'rgba(255,122,26,0.16)' : 'transparent',
                                                    }}
                                                >
                                                    {security ? <SecurityRoundedIcon /> : unread ? <WarningAmberRoundedIcon /> : <ReceiptLongRoundedIcon />}
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontWeight: 900 }}>
                                                        {notificationTitle(item, lang)}
                                                    </Typography>
                                                    <Typography sx={{ color: 'text.secondary', mt: 0.4 }}>
                                                        {notificationBody(item, lang)}
                                                    </Typography>
                                                    {notificationTime(item) && (
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, mt: 0.8 }}>
                                                            {notificationTime(item)}
                                                        </Typography>
                                                    )}
                                                    {security && (
                                                        <Button
                                                            size="small"
                                                            onClick={() => setDetailsItem(item)}
                                                            sx={{ color: '#FF8A00', textTransform: 'none', fontWeight: 900, mt: 0.8, px: 0 }}
                                                        >
                                                            {notificationActionLabel(item, lang)}
                                                        </Button>
                                                    )}
                                                    {!security && notificationActionUrl(item) && (
                                                        <Button
                                                            size="small"
                                                            onClick={() => navigate(notificationActionUrl(item))}
                                                            sx={{ color: '#FF8A00', textTransform: 'none', fontWeight: 900, mt: 0.8, px: 0 }}
                                                        >
                                                            {notificationActionLabel(item, lang)}
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </Box>
                                        </React.Fragment>
                                    )
                                })}
                            </Stack>
                        )}
                    </CardContent>
                </Card>

                <Button
                    variant="text"
                    onClick={() => navigate('/transactions')}
                    sx={{ alignSelf: 'flex-start', color: '#FF8A00', textTransform: 'none', fontWeight: 900 }}
                >
                    {isFr ? 'Voir les transactions' : 'View Transactions'}
                </Button>
            </Stack>
            <SecurityDetailsDialog
                item={detailsItem}
                lang={lang}
                onClose={() => setDetailsItem(null)}
            />
        </Container>
    )
}

const detailMap = (item) => {
    const data = notificationData(item)
    return data.details && typeof data.details === 'object' ? data.details : data
}

const detailText = (details, data, keys, fallback) => {
    for (const source of [details, data]) {
        for (const key of keys) {
            const value = source?.[key]
            if (value !== undefined && value !== null && String(value).trim() && String(value) !== 'null') {
                return String(value)
            }
        }
    }
    return fallback
}

const formatDetailTime = (value, lang) => {
    if (!value) return lang === 'fr' ? 'Non disponible' : 'Not available'
    try {
        return new Date(value).toLocaleString(lang === 'fr' ? 'fr-FR' : undefined)
    } catch {
        return value
    }
}

function SecurityDetailsDialog({ item, lang, onClose }) {
    const open = Boolean(item)
    const data = notificationData(item)
    const details = detailMap(item)
    const isFr = lang === 'fr'
    const fallback = isFr ? 'Non disponible' : 'Not available'
    const lat = Number(details?.latitude ?? details?.lat)
    const lon = Number(details?.longitude ?? details?.lon)
    const location = detailText(details, data, ['approximate_location', 'location_label', 'city'], fallback)
    const hasMap = Number.isFinite(lat) && Number.isFinite(lon)

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{
            sx: {
                borderRadius: 5,
                bgcolor: '#120B24',
                border: '1px solid rgba(255,122,26,0.28)',
                color: '#fff',
            },
        }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Stack direction="row" spacing={1.3} alignItems="center">
                    <Box sx={{ width: 42, height: 42, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,122,26,0.16)' }}>
                        <SecurityRoundedIcon sx={{ color: '#FF8A00' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 900 }}>
                        {isFr ? 'Details de connexion' : 'Sign-in Details'}
                    </Typography>
                </Stack>
                <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
                <Stack spacing={1.4}>
                    <DetailRow label={isFr ? 'Adresse IP' : 'IP address'} value={detailText(details, data, ['ip_address'], fallback)} />
                    <DetailRow label={isFr ? 'Appareil' : 'Device type'} value={detailText(details, data, ['device_type'], fallback)} />
                    <DetailRow label={isFr ? 'Navigateur/app' : 'Browser/app'} value={detailText(details, data, ['browser_app'], fallback)} />
                    <DetailRow label={isFr ? 'Fournisseur' : 'ISP/provider'} value={detailText(details, data, ['isp', 'provider'], fallback)} />
                    <DetailRow label={isFr ? 'Position approx.' : 'Approx. location'} value={location} />
                    <DetailRow label={isFr ? 'Heure' : 'Login time'} value={formatDetailTime(detailText(details, data, ['login_time', 'occurred_at'], ''), lang)} />
                    {hasMap && (
                        <>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1 }} />
                            <Box
                                component="img"
                                alt={location}
                                src={`https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lon}&zoom=10&size=600x240&markers=${lat},${lon},red-pushpin`}
                                sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)' }}
                            />
                        </>
                    )}
                    {!hasMap && (
                        <Box sx={{ mt: 1, p: 2, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <MapRoundedIcon sx={{ color: '#FF8A00' }} />
                                <Typography sx={{ fontWeight: 800 }}>{location}</Typography>
                            </Stack>
                            <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                                {isFr ? 'Zone approximative uniquement.' : 'Approximate area only.'}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function DetailRow({ label, value }) {
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8}>
            <Typography sx={{ color: 'rgba(255,255,255,0.52)', width: { sm: 150 }, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography sx={{ color: '#fff', fontWeight: 800 }}>
                {value}
            </Typography>
        </Stack>
    )
}
